import React, { useCallback, useEffect, useState } from 'react';
import { Banner, EmptyState, Select, Spinner, useToast } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { useProperty } from '../../app/PropertyContext';
import { usersApi } from '../users/usersApi';
import { settingsApi } from './settingsApi';
import ApprovalWorkflowForm from './ApprovalWorkflowForm';

export default function ApprovalWorkflowsTab() {
  const toast = useToast();
  const t = useT();
  const { properties, loaded: propsLoaded } = useProperty();

  const [roles, setRoles] = useState([]);
  const [org, setOrg] = useState(null);
  const [orgLoading, setOrgLoading] = useState(true);
  const [orgSaving, setOrgSaving] = useState(false);

  const [selectedProperty, setSelectedProperty] = useState('');
  const [propSettings, setPropSettings] = useState(null);
  const [propLoading, setPropLoading] = useState(false);
  const [propSaving, setPropSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [rolesRes, orgRes] = await Promise.all([usersApi.listRoles(), settingsApi.getOrgApproval()]);
        setRoles(Array.isArray(rolesRes) ? rolesRes : rolesRes?.items ?? []);
        setOrg(orgRes);
      } catch (err) {
        toast.error(t('Could not load approval settings'), { description: err?.message });
      } finally {
        setOrgLoading(false);
      }
    })();
  }, [toast, t]);

  const loadProperty = useCallback(async (propertyId) => {
    setSelectedProperty(propertyId);
    setPropSettings(null);
    if (!propertyId) return;
    setPropLoading(true);
    try {
      setPropSettings(await settingsApi.getPropertyApproval(propertyId));
    } catch (err) {
      toast.error(t('Could not load property approval settings'), { description: err?.message });
    } finally {
      setPropLoading(false);
    }
  }, [toast, t]);

  const handleSaveOrg = async (payload) => {
    setOrgSaving(true);
    try {
      setOrg(await settingsApi.updateOrgApproval(payload));
      toast.success(t('Approval workflows saved'));
    } catch (err) {
      toast.error(t('Could not save workflows'), { description: err?.message });
    } finally {
      setOrgSaving(false);
    }
  };

  const handleSaveProperty = async (payload) => {
    if (!selectedProperty) return;
    setPropSaving(true);
    try {
      const res = await settingsApi.updatePropertyApproval(selectedProperty, { propertyId: selectedProperty, ...payload });
      setPropSettings(res);
      toast.success(t('Property approval overrides saved'));
    } catch (err) {
      toast.error(t('Could not save overrides'), { description: err?.message });
    } finally {
      setPropSaving(false);
    }
  };

  if (orgLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={24} /></div>;
  }

  const orgCurrency = org?.defaultCurrency || org?.currencies?.[0] || 'USD';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <section>
        <h2 className="appr-scope__title">{t('Organization defaults')}</h2>
        <ApprovalWorkflowForm
          key="org"
          mode="org"
          initial={org}
          roles={roles}
          fallbackCurrency={orgCurrency}
          saving={orgSaving}
          onSave={handleSaveOrg}
        />
      </section>

      <section>
        <h2 className="appr-scope__title">{t('Property overrides')}</h2>
        {!propsLoaded ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={24} /></div>
        ) : properties.length === 0 ? (
          <EmptyState
            icon={<Icon.Building size={28} />}
            title={t('No properties yet')}
            description={t('Once a property exists, you can override the organization approval workflows for it.')}
          />
        ) : (
          <>
            <div className="set-property-pick">
              <Select
                options={[{ value: '', label: t('Select a property…') }, ...properties.map((p) => ({ value: p.id, label: p.name }))]}
                value={selectedProperty}
                onChange={loadProperty}
                ariaLabel={t('Property')}
                searchable
              />
            </div>

            {!selectedProperty ? (
              <Banner tone="info">{t('Choose a property to override its approval workflows. Left as inherit, it uses the organization defaults.')}</Banner>
            ) : propLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={24} /></div>
            ) : propSettings ? (
              <ApprovalWorkflowForm
                key={selectedProperty}
                mode="property"
                initial={propSettings}
                roles={roles}
                fallbackCurrency={orgCurrency}
                saving={propSaving}
                onSave={handleSaveProperty}
              />
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}
